import {
  Box,
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Divider,
} from '@mantine/core';
import {
  IconBriefcase,
  IconClipboardText,
  IconTruck,
  IconFileText,
  IconTicket,
  IconLayoutKanban,
  IconUser,
  IconUsers,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconLayoutGrid,
  IconPackage,
  IconTruckDelivery,
  IconServer,
  IconTool,
  IconBook,
  IconUserCheck,
  IconCar,
  IconChecklist,
  IconArrowRight,
  IconPencil,
  IconCheck,
  IconX,
  IconPlus,
  IconGripVertical,
  IconRefresh,
} from '@tabler/icons-react';
import type { NavigateFunction } from 'react-router-dom';
import type { MantineTheme } from '@mantine/core';
import { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuthStore } from '../../stores/auth';
import { useDashboardPreferences } from '../../stores/dashboardPreferences';
import styles from '../DashboardPage.module.css';

type UserRole = 'TECHNICIAN' | 'TEAMLEAD' | 'ADMIN';

interface ActionCardDef {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  gradient: string;
  roles?: UserRole[];
}

const ALL_CARDS: ActionCardDef[] = [
  // Hauptworkflows
  {
    id: 'customer-hub',
    title: 'Kunden-Hub',
    description: 'Angebote, Garantie, Bestellungen & Material im Kundenkontext',
    icon: IconBriefcase,
    path: '/customer-hub',
    gradient: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)',
  },
  {
    id: 'service-report',
    title: 'Service Report',
    description: 'Service-Einsätze vor Ort dokumentieren mit Zeiterfassung & Signatur',
    icon: IconClipboardText,
    path: '/service-report',
    gradient: 'linear-gradient(135deg, #ff6600 0%, #cc5200 100%)',
  },
  {
    id: 'delivery-report',
    title: 'Delivery Report',
    description: 'Lieferungen erfassen mit Barcode-Scan und Empfangsbestätigung',
    icon: IconTruck,
    path: '/delivery-report',
    gradient: 'linear-gradient(135deg, #22b8cf 0%, #15aabf 100%)',
  },
  {
    id: 'submissions',
    title: 'Meine Submissions',
    description: 'Übersicht aller eingereichten Reports und deren Status',
    icon: IconFileText,
    path: '/submissions',
    gradient: 'linear-gradient(135deg, #be4bdb 0%, #9c36b5 100%)',
  },
  // Tickets
  {
    id: 'new-ticket',
    title: 'Neues Ticket',
    description: 'Schnell ein neues Support-Ticket erstellen',
    icon: IconTicket,
    path: '/tickets/new',
    gradient: 'linear-gradient(135deg, #228be6 0%, #1971c2 100%)',
  },
  {
    id: 'ticket-board',
    title: 'Ticket Board',
    description: 'Alle Tickets in der Kanban-Übersicht',
    icon: IconLayoutKanban,
    path: '/tickets/board',
    gradient: 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)',
  },
  {
    id: 'my-tickets',
    title: 'Meine Tickets',
    description: 'Persönliche Ticket-Liste mit Status und Priorität',
    icon: IconUser,
    path: '/my-tickets',
    gradient: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)',
  },
  {
    id: 'team-tickets',
    title: 'Team Tickets',
    description: 'Gemeinsame Tickets des gesamten Teams',
    icon: IconUsers,
    path: '/team-tickets',
    gradient: 'linear-gradient(135deg, #20c997 0%, #12b886 100%)',
  },
  {
    id: 'reminders',
    title: 'Aufgaben & Erinnerungen',
    description: 'Was heute fällig ist — eigene Erinnerungen und Aufgaben der Leitung',
    icon: IconChecklist,
    path: '/reminders',
    gradient: 'linear-gradient(135deg, #f59f00 0%, #e67700 100%)',
  },
  // Planung & Zeit
  {
    id: 'calendar',
    title: 'Kalender',
    description: 'Jour fixe, Projekt- und Team-Termine — wer ist wann wo',
    icon: IconCalendarEvent,
    path: '/calendar',
    gradient: 'linear-gradient(135deg, #7950f2 0%, #5f3dc4 100%)',
  },
  {
    id: 'appointments',
    title: 'Terminplanung',
    description: 'Einsätze planen und Termine koordinieren',
    icon: IconCalendar,
    path: '/appointments',
    gradient: 'linear-gradient(135deg, #20c997 0%, #099268 100%)',
  },
  {
    id: 'time-booking',
    title: 'Zeitbuchung',
    description: 'Arbeitszeiten buchen und verwalten',
    icon: IconClock,
    path: '/time-booking',
    gradient: 'linear-gradient(135deg, #cc5de8 0%, #9c36b5 100%)',
    roles: ['TEAMLEAD', 'ADMIN'],
  },
  {
    id: 'dispatch',
    title: 'Dispatch Board',
    description: 'Einsätze disponieren und zuweisen',
    icon: IconLayoutGrid,
    path: '/dispatch',
    gradient: 'linear-gradient(135deg, #845ef7 0%, #6741d9 100%)',
  },
  // Material & Lager
  {
    id: 'technician-requests',
    title: 'Materialanfragen',
    description: 'Material anfordern und den Bearbeitungsstatus verfolgen',
    icon: IconPackage,
    path: '/technician-requests',
    gradient: 'linear-gradient(135deg, #fd7e14 0%, #e03131 100%)',
  },
  {
    id: 'goods-receipts',
    title: 'Wareneingang',
    description: 'Wareneingänge erfassen und buchen',
    icon: IconTruckDelivery,
    path: '/goods-receipts',
    gradient: 'linear-gradient(135deg, #f76707 0%, #d9480f 100%)',
  },
  {
    id: 'inventory',
    title: 'Lager & Inventar',
    description: 'Lagerbestände prüfen und Inventur durchführen',
    icon: IconPackage,
    path: '/inventory',
    gradient: 'linear-gradient(135deg, #40c057 0%, #2f9e44 100%)',
  },
  // Übersicht
  {
    id: 'infrastructure',
    title: 'Infrastruktur',
    description: 'Kundensysteme und Netzwerkinfrastruktur einsehen',
    icon: IconServer,
    path: '/infrastructure',
    gradient: 'linear-gradient(135deg, #845ef7 0%, #5c3d99 100%)',
  },
  {
    id: 'maintenance',
    title: 'Wartungskunden',
    description: 'Wartungsverträge und -aufgaben je Kunde',
    icon: IconTool,
    path: '/maintenance',
    gradient: 'linear-gradient(135deg, #20c997 0%, #0c8599 100%)',
  },
  {
    id: 'tools',
    title: 'Tools-Library',
    description: 'Interne Tools, Skripte und Ressourcen',
    icon: IconBook,
    path: '/tools',
    gradient: 'linear-gradient(135deg, #15aabf 0%, #0c8599 100%)',
  },
  {
    id: 'contact-suggestions',
    title: 'AP-Vorschläge',
    description: 'Kontaktvorschläge für Kunden prüfen und bestätigen',
    icon: IconUserCheck,
    path: '/contact-suggestions',
    gradient: 'linear-gradient(135deg, #845ef7 0%, #4c6ef5 100%)',
  },
  {
    id: 'my-trips',
    title: 'Meine Fahrten',
    description: 'Eigene Fahrtenhistorie und -details',
    icon: IconCar,
    path: '/my-trips',
    gradient: 'linear-gradient(135deg, #228be6 0%, #1864ab 100%)',
  },
];

// ─── Single sortable card ────────────────────────────────────────────────────

interface SortableCardProps {
  card: ActionCardDef;
  editMode: boolean;
  draftsCount: number;
  navigate: NavigateFunction;
  theme: MantineTheme;
  onHide: (id: string) => void;
}

function SortableCard({ card, editMode, draftsCount, navigate, theme, onHide }: SortableCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, height: '100%' }}>
      <Card
        className={`${styles.actionCard} ${editMode ? styles.actionCardEdit : ''}`}
        onClick={editMode ? undefined : () => navigate(card.path)}
        padding="xl"
        h="100%"
      >
        <Stack gap="md" justify="space-between" h="100%">
          <Group justify="space-between" align="flex-start">
            <Group gap="xs">
              {editMode && (
                <Box
                  {...attributes}
                  {...listeners}
                  style={{ cursor: 'grab', color: 'var(--mantine-color-gray-5)', lineHeight: 0, paddingTop: 2 }}
                >
                  <IconGripVertical size={16} />
                </Box>
              )}
              <ThemeIcon size={50} radius="md" style={{ background: card.gradient }}>
                <card.icon size={26} />
              </ThemeIcon>
            </Group>

            {editMode ? (
              <Tooltip label="Ausblenden" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  radius="xl"
                  onClick={() => onHide(card.id)}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <IconArrowRight
                size={20}
                className={styles.arrowIcon}
                color={theme.colors.gray[5]}
              />
            )}
          </Group>

          <div>
            <Text size="lg" fw={600} c="white">
              {card.title}
            </Text>
            <Text size="sm" c="dimmed" mt="xs">
              {card.description}
            </Text>
          </div>

          {card.id === 'submissions' && draftsCount > 0 && (
            <Badge color="orange" variant="light" size="sm">
              {draftsCount} Entwürfe
            </Badge>
          )}
        </Stack>
      </Card>
    </div>
  );
}

// ─── Hidden card chip ────────────────────────────────────────────────────────

interface HiddenCardChipProps {
  card: ActionCardDef;
  onShow: (id: string) => void;
}

function HiddenCardChip({ card, onShow }: HiddenCardChipProps) {
  return (
    <Card className={styles.hiddenCard} padding="sm" onClick={() => onShow(card.id)}>
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={28} radius="sm" style={{ background: card.gradient, flexShrink: 0 }}>
          <card.icon size={14} />
        </ThemeIcon>
        <Text size="sm" c="dimmed" style={{ flex: 1 }} truncate>
          {card.title}
        </Text>
        <ActionIcon variant="subtle" color="green" size="sm" radius="xl">
          <IconPlus size={14} />
        </ActionIcon>
      </Group>
    </Card>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface DashboardActionCardsProps {
  navigate: NavigateFunction;
  theme: MantineTheme;
  draftsCount: number;
}

export function DashboardActionCards({ navigate, theme, draftsCount }: DashboardActionCardsProps) {
  const { user } = useAuthStore();
  const { cardOrder, hiddenCardIds, setCardOrder, toggleCard, reset } = useDashboardPreferences();
  const [editMode, setEditMode] = useState(false);

  const userRole = (user?.role ?? 'TECHNICIAN') as UserRole;

  const availableCards = ALL_CARDS.filter(
    (c) => !c.roles || c.roles.includes(userRole),
  );

  const visibleCards = cardOrder
    .map((id) => availableCards.find((c) => c.id === id))
    .filter((c): c is ActionCardDef => c !== undefined && !hiddenCardIds.includes(c.id));

  const hiddenCards = availableCards.filter(
    (c) => hiddenCardIds.includes(c.id) || !cardOrder.includes(c.id),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = cardOrder.indexOf(String(active.id));
        const newIndex = cardOrder.indexOf(String(over.id));
        setCardOrder(arrayMove(cardOrder, oldIndex, newIndex));
      }
    },
    [cardOrder, setCardOrder],
  );

  return (
    <Box>
      {/* Header with edit controls */}
      <Group justify="space-between" mb="md">
        <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
          Schnellaktionen
        </Text>
        <Group gap="xs">
          {editMode && (
            <Tooltip label="Standard wiederherstellen" withArrow>
              <ActionIcon variant="subtle" color="gray" size="sm" onClick={reset}>
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={editMode ? 'Fertig' : 'Anpassen'} withArrow>
            <ActionIcon
              variant={editMode ? 'filled' : 'subtle'}
              color={editMode ? 'green' : 'gray'}
              size="sm"
              onClick={() => setEditMode((v) => !v)}
            >
              {editMode ? <IconCheck size={14} /> : <IconPencil size={14} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Visible & sortable cards */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={cardOrder} strategy={rectSortingStrategy}>
          <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
            {visibleCards.map((card) => (
              <SortableCard
                key={card.id}
                card={card}
                editMode={editMode}
                draftsCount={draftsCount}
                navigate={navigate}
                theme={theme}
                onHide={toggleCard}
              />
            ))}
          </SimpleGrid>
        </SortableContext>
      </DndContext>

      {/* Hidden cards (only visible in edit mode) */}
      {editMode && hiddenCards.length > 0 && (
        <Box mt="lg">
          <Divider
            label={
              <Text size="xs" c="dimmed">
                Ausgeblendet — klicken zum Hinzufügen
              </Text>
            }
            mb="sm"
          />
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="sm">
            {hiddenCards.map((card) => (
              <HiddenCardChip key={card.id} card={card} onShow={toggleCard} />
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
}
